import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getTenant, updateTenant } from '../services/api';

interface ConfigSettingsProps {
  skipLayout?: boolean;
}

const ConfigSettings: React.FC<ConfigSettingsProps> = ({ skipLayout = false }) => {
  const { user, canModifyTenantSettings } = useAuth();
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [isDevMode, setIsDevMode] = useState(false);

  // --- Vehicle Movement Threshold state ---
  const [movementThreshold, setMovementThreshold] = useState<number>(10);
  const [originalThreshold, setOriginalThreshold] = useState<number>(10);
  const [editingMovement, setEditingMovement] = useState(false);
  const [savingMovement, setSavingMovement] = useState(false);

  // --- Strict Date Attendance state ---
  const [strictDateAttendance, setStrictDateAttendance] = useState(false);
  const [originalStrictDate, setOriginalStrictDate] = useState(false);
  const [editingStrictDate, setEditingStrictDate] = useState(false);
  const [savingStrictDate, setSavingStrictDate] = useState(false);

  // --- Halt Report: Min Halt Time state ---
  const [haltMinTime, setHaltMinTime] = useState<number>(1200);
  const [originalHaltMinTime, setOriginalHaltMinTime] = useState<number>(1200);
  const [editingHaltMinTime, setEditingHaltMinTime] = useState(false);
  const [savingHaltMinTime, setSavingHaltMinTime] = useState(false);

  // --- Halt Report: Max Speed state ---
  const [haltMaxSpeed, setHaltMaxSpeed] = useState<number>(2);
  const [originalHaltMaxSpeed, setOriginalHaltMaxSpeed] = useState<number>(2);
  const [editingHaltMaxSpeed, setEditingHaltMaxSpeed] = useState(false);
  const [savingHaltMaxSpeed, setSavingHaltMaxSpeed] = useState(false);

  // --- Halt Report: Min Distance state ---
  const [haltMinDistance, setHaltMinDistance] = useState<number>(5);
  const [originalHaltMinDistance, setOriginalHaltMinDistance] = useState<number>(5);
  const [editingHaltMinDistance, setEditingHaltMinDistance] = useState(false);
  const [savingHaltMinDistance, setSavingHaltMinDistance] = useState(false);

  // --- Convert knots to km/h (dev-only) ---
  const [convertKnotsToKmph, setConvertKnotsToKmph] = useState(false);
  const [originalConvertKnotsToKmph, setOriginalConvertKnotsToKmph] = useState(false);
  const [editingConvertKnotsToKmph, setEditingConvertKnotsToKmph] = useState(false);
  const [savingConvertKnotsToKmph, setSavingConvertKnotsToKmph] = useState(false);

  // --- Dashboard route live refresh (Current Route in-trip + Day Route idle) ---
  const [dashboardRouteDataRefreshSeconds, setDashboardRouteDataRefreshSeconds] = useState<number>(15);
  const [originalDashboardRouteDataRefreshSeconds, setOriginalDashboardRouteDataRefreshSeconds] = useState<number>(15);
  const [editingDashboardRouteDataRefresh, setEditingDashboardRouteDataRefresh] = useState(false);
  const [savingDashboardRouteDataRefresh, setSavingDashboardRouteDataRefresh] = useState(false);

  useEffect(() => {
    const checkHash = () => setIsDevMode(window.location.hash === '#dev');
    checkHash();
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, []);

  useEffect(() => {
    const fetchTenant = async () => {
      if (!user?.tenantId) {
        setError('No tenant associated with your account');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await getTenant(user.tenantId);
        if (response.data.success && response.data.data) {
          const tenantData = response.data.data;
          setTenantId(tenantData._id);
          const threshold = tenantData.spec.configSettings?.vehicleMovementThresholdMeters ?? 10;
          setMovementThreshold(threshold);
          setOriginalThreshold(threshold);
          const strict = tenantData.spec.configSettings?.strictDateAttendance ?? false;
          setStrictDateAttendance(strict);
          setOriginalStrictDate(strict);
          const hMinT = tenantData.spec.configSettings?.haltReportMinHaltTimeSeconds ?? 1200;
          setHaltMinTime(hMinT);
          setOriginalHaltMinTime(hMinT);
          const hMaxS = tenantData.spec.configSettings?.haltReportMaxSpeedKmph ?? 2;
          setHaltMaxSpeed(hMaxS);
          setOriginalHaltMaxSpeed(hMaxS);
          const hMinD = tenantData.spec.configSettings?.haltReportMinDistanceMeters ?? 5;
          setHaltMinDistance(hMinD);
          setOriginalHaltMinDistance(hMinD);
          const convertKnots = tenantData.spec.configSettings?.convertKnotsToKmph ?? false;
          setConvertKnotsToKmph(convertKnots);
          setOriginalConvertKnotsToKmph(convertKnots);
          const cs = tenantData.spec.configSettings;
          const refreshRaw = cs?.dashboardRouteDataRefreshMs ?? cs?.currentRouteDataRefreshMs;
          const refreshMs = typeof refreshRaw === 'number' && Number.isFinite(refreshRaw) && refreshRaw > 0
            ? Math.max(refreshRaw, 10_000)
            : 15_000;
          const refreshSeconds = Math.round(refreshMs / 1000);
          setDashboardRouteDataRefreshSeconds(refreshSeconds);
          setOriginalDashboardRouteDataRefreshSeconds(refreshSeconds);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [user?.tenantId]);

  // --- Movement Threshold handlers ---
  const handleSaveMovement = async () => {
    if (!canModifyTenantSettings) return;
    if (!tenantId) return;
    if (movementThreshold < 1 || movementThreshold > 1000) {
      setError('Vehicle Movement Threshold must be between 1 and 1000 meters');
      return;
    }
    try {
      setSavingMovement(true);
      setError('');
      const response = await updateTenant(tenantId, {
        spec: { configSettings: { vehicleMovementThresholdMeters: movementThreshold } },
      });
      if (response.data.success) {
        setOriginalThreshold(movementThreshold);
        setEditingMovement(false);
        setSuccess('Vehicle Movement Threshold updated successfully');
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingMovement(false);
    }
  };

  const handleCancelMovement = () => {
    setMovementThreshold(originalThreshold);
    setEditingMovement(false);
    setError('');
  };

  // --- Strict Date Attendance handlers ---
  const handleSaveStrictDate = async () => {
    if (!canModifyTenantSettings) return;
    if (!tenantId) return;
    try {
      setSavingStrictDate(true);
      setError('');
      const response = await updateTenant(tenantId, {
        spec: { configSettings: { strictDateAttendance } },
      });
      if (response.data.success) {
        setOriginalStrictDate(strictDateAttendance);
        setEditingStrictDate(false);
        setSuccess('Strict Date Attendance updated successfully');
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingStrictDate(false);
    }
  };

  const handleCancelStrictDate = () => {
    setStrictDateAttendance(originalStrictDate);
    setEditingStrictDate(false);
    setError('');
  };

  // --- Halt Report: Min Halt Time handlers ---
  const handleSaveHaltMinTime = async () => {
    if (!canModifyTenantSettings) return;
    if (!tenantId) return;
    if (haltMinTime < 1 || haltMinTime > 86400) {
      setError('Min Halt Time must be between 1 and 86400 seconds');
      return;
    }
    try {
      setSavingHaltMinTime(true);
      setError('');
      const response = await updateTenant(tenantId, {
        spec: { configSettings: { haltReportMinHaltTimeSeconds: haltMinTime } },
      });
      if (response.data.success) {
        setOriginalHaltMinTime(haltMinTime);
        setEditingHaltMinTime(false);
        setSuccess('Halt Report Min Halt Time updated successfully');
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingHaltMinTime(false);
    }
  };

  const handleCancelHaltMinTime = () => {
    setHaltMinTime(originalHaltMinTime);
    setEditingHaltMinTime(false);
    setError('');
  };

  // --- Halt Report: Max Speed handlers ---
  const handleSaveHaltMaxSpeed = async () => {
    if (!canModifyTenantSettings) return;
    if (!tenantId) return;
    if (haltMaxSpeed < 0 || haltMaxSpeed > 100) {
      setError('Max Speed must be between 0 and 100 km/h');
      return;
    }
    try {
      setSavingHaltMaxSpeed(true);
      setError('');
      const response = await updateTenant(tenantId, {
        spec: { configSettings: { haltReportMaxSpeedKmph: haltMaxSpeed } },
      });
      if (response.data.success) {
        setOriginalHaltMaxSpeed(haltMaxSpeed);
        setEditingHaltMaxSpeed(false);
        setSuccess('Halt Report Max Speed updated successfully');
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingHaltMaxSpeed(false);
    }
  };

  const handleCancelHaltMaxSpeed = () => {
    setHaltMaxSpeed(originalHaltMaxSpeed);
    setEditingHaltMaxSpeed(false);
    setError('');
  };

  // --- Halt Report: Min Distance handlers ---
  const handleSaveHaltMinDistance = async () => {
    if (!canModifyTenantSettings) return;
    if (!tenantId) return;
    if (haltMinDistance < 1 || haltMinDistance > 10000) {
      setError('Min Distance must be between 1 and 10000 meters');
      return;
    }
    try {
      setSavingHaltMinDistance(true);
      setError('');
      const response = await updateTenant(tenantId, {
        spec: { configSettings: { haltReportMinDistanceMeters: haltMinDistance } },
      });
      if (response.data.success) {
        setOriginalHaltMinDistance(haltMinDistance);
        setEditingHaltMinDistance(false);
        setSuccess('Halt Report Min Distance updated successfully');
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingHaltMinDistance(false);
    }
  };

  const handleCancelHaltMinDistance = () => {
    setHaltMinDistance(originalHaltMinDistance);
    setEditingHaltMinDistance(false);
    setError('');
  };

  // --- Convert knots to km/h (dev-only) handlers ---
  const handleSaveConvertKnotsToKmph = async () => {
    if (!canModifyTenantSettings) return;
    if (!tenantId) return;
    try {
      setSavingConvertKnotsToKmph(true);
      setError('');
      await updateTenant(tenantId, {
        spec: { configSettings: { convertKnotsToKmph } },
      });
      setOriginalConvertKnotsToKmph(convertKnotsToKmph);
      setEditingConvertKnotsToKmph(false);
      setSuccess('Convert speed from knots to km/h updated');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingConvertKnotsToKmph(false);
    }
  };

  const handleCancelConvertKnotsToKmph = () => {
    setConvertKnotsToKmph(originalConvertKnotsToKmph);
    setEditingConvertKnotsToKmph(false);
    setError('');
  };

  // --- Dashboard route live refresh handlers ---
  const handleSaveDashboardRouteDataRefresh = async () => {
    if (!canModifyTenantSettings) return;
    if (!tenantId) return;
    if (dashboardRouteDataRefreshSeconds < 10) {
      setError('Dashboard Route Date Refresh must be at least 10 seconds');
      return;
    }
    try {
      setSavingDashboardRouteDataRefresh(true);
      setError('');
      const refreshMs = Math.max(10_000, Math.round(dashboardRouteDataRefreshSeconds * 1000));
      await updateTenant(tenantId, {
        spec: {
          configSettings: {
            dashboardRouteDataRefreshMs: refreshMs,
            currentRouteDataRefreshMs: refreshMs,
          },
        },
      });
      const savedSeconds = Math.round(refreshMs / 1000);
      setDashboardRouteDataRefreshSeconds(savedSeconds);
      setOriginalDashboardRouteDataRefreshSeconds(savedSeconds);
      setEditingDashboardRouteDataRefresh(false);
      setSuccess('Dashboard Route Date Refresh updated successfully');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingDashboardRouteDataRefresh(false);
    }
  };

  const handleCancelDashboardRouteDataRefresh = () => {
    setDashboardRouteDataRefreshSeconds(originalDashboardRouteDataRefreshSeconds);
    setEditingDashboardRouteDataRefresh(false);
    setError('');
  };

  if (loading) {
    const loadingContent = <div className="loading">Loading settings...</div>;
    if (skipLayout) return loadingContent;
    return <DashboardLayout>{loadingContent}</DashboardLayout>;
  }

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 270px)' }}>
      {/* Messages */}
      <div style={{ flexShrink: 0 }}>
        {error && <div className="error-message">{error}</div>}
        {success && (
          <div className="success-message" style={{ backgroundColor: '#d4edda', color: '#155724', padding: '10px 14px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
            {success}
          </div>
        )}
      </div>

      {/* Settings Table */}
      <div className="table-container" style={{ height: 'calc(100vh - 350px)', overflow: 'auto' }}>
        <table className="bookings-table">
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Value</th>
              {canModifyTenantSettings && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>

            {/* Vehicle Movement Threshold */}
            <tr>
              <td>
                <strong>Vehicle Movement Threshold</strong>
              </td>
              <td style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b', lineHeight: '1.4' }}>
                Minimum distance (meters) between GPS points to count as moving. Default: 10m
              </td>
              <td>
                {editingMovement ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input
                        type="number"
                        min={1}
                        max={1000}
                        step={1}
                        value={movementThreshold}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val)) setMovementThreshold(val);
                        }}
                        style={{
                          width: '70px',
                          padding: '4px 8px',
                          backgroundColor: isDarkTheme ? '#0f172a' : '#ffffff',
                          color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                          border: `1px solid ${isDarkTheme ? '#475569' : '#cbd5e1'}`,
                          borderRadius: '5px',
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center',
                          outline: 'none',
                        }}
                      />
                      <span style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>meters</span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {[5, 10, 20, 50, 100].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setMovementThreshold(p)}
                          style={{
                            padding: '2px 8px',
                            fontSize: '12px',
                            fontWeight: 500,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            border: movementThreshold === p ? 'none' : `1px solid ${isDarkTheme ? '#475569' : '#cbd5e1'}`,
                            backgroundColor: movementThreshold === p ? '#2196f3' : 'transparent',
                            color: movementThreshold === p ? '#fff' : (isDarkTheme ? '#94a3b8' : '#64748b'),
                          }}
                        >
                          {p}m
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <strong>{originalThreshold} meters</strong>
                )}
              </td>
              {canModifyTenantSettings && (
              <td>
                <div className="action-buttons">
                  {editingMovement ? (
                    <>
                      <button
                        className="btn-icon"
                        onClick={handleCancelMovement}
                        style={{ background: isDarkTheme ? '#334155' : '#e2e8f0', color: isDarkTheme ? '#94a3b8' : '#64748b' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={handleSaveMovement}
                        disabled={savingMovement || movementThreshold === originalThreshold}
                        style={{
                          opacity: savingMovement || movementThreshold === originalThreshold ? 0.5 : 1,
                          cursor: savingMovement || movementThreshold === originalThreshold ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {savingMovement ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  ) : canModifyTenantSettings ? (
                    <button className="btn-icon btn-edit" onClick={() => setEditingMovement(true)}>
                      Edit
                    </button>
                    ) : null}
                </div>
              </td>
              )}
            </tr>

            {/* Strict Date Attendance */}
            <tr>
              <td>
                <strong>Strict Date Attendance</strong>
              </td>
              <td style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b', lineHeight: '1.4' }}>
                When enabled, attendance records are strictly bound to the selected date. Default: Off
              </td>
              <td>
                {editingStrictDate ? (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={strictDateAttendance}
                      onChange={(e) => setStrictDateAttendance(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2196f3' }}
                    />
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: strictDateAttendance
                        ? (isDarkTheme ? '#6ee7b7' : '#059669')
                        : (isDarkTheme ? '#f87171' : '#dc2626'),
                    }}>
                      {strictDateAttendance ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                ) : (
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: '5px',
                    fontSize: '13px',
                    fontWeight: 600,
                    backgroundColor: originalStrictDate
                      ? (isDarkTheme ? 'rgba(16,185,129,0.15)' : '#ecfdf5')
                      : (isDarkTheme ? 'rgba(239,68,68,0.15)' : '#fef2f2'),
                    color: originalStrictDate
                      ? (isDarkTheme ? '#6ee7b7' : '#059669')
                      : (isDarkTheme ? '#fca5a5' : '#dc2626'),
                    border: originalStrictDate
                      ? (isDarkTheme ? '1px solid rgba(16,185,129,0.3)' : '1px solid #d1fae5')
                      : (isDarkTheme ? '1px solid rgba(239,68,68,0.3)' : '1px solid #fecaca'),
                  }}>
                    {originalStrictDate ? 'Enabled' : 'Disabled'}
                  </span>
                )}
              </td>
              {canModifyTenantSettings && (
              <td>
                <div className="action-buttons">
                  {editingStrictDate ? (
                    <>
                      <button
                        className="btn-icon"
                        onClick={handleCancelStrictDate}
                        style={{ background: isDarkTheme ? '#334155' : '#e2e8f0', color: isDarkTheme ? '#94a3b8' : '#64748b' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={handleSaveStrictDate}
                        disabled={savingStrictDate || strictDateAttendance === originalStrictDate}
                        style={{
                          opacity: savingStrictDate || strictDateAttendance === originalStrictDate ? 0.5 : 1,
                          cursor: savingStrictDate || strictDateAttendance === originalStrictDate ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {savingStrictDate ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  ) : canModifyTenantSettings ? (
                    <button className="btn-icon btn-edit" onClick={() => setEditingStrictDate(true)}>
                      Edit
                    </button>
                    ) : null}
                </div>
              </td>
              )}
            </tr>

            {/* Dashboard Route Date Refresh (tracking: Current Route in-trip + Day Route idle) */}
            <tr>
              <td>
                <strong>Dashboard Route Date Refresh</strong>
              </td>
              <td style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b', lineHeight: '1.4' }}>
                How often live GPS is refreshed on the tracking map for Current Route (active trip) and Day Route when playback is stopped. Default: 15 seconds, minimum: 10 seconds
              </td>
              <td>
                {editingDashboardRouteDataRefresh ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="number"
                      min={10}
                      step={1}
                      value={dashboardRouteDataRefreshSeconds}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) setDashboardRouteDataRefreshSeconds(val);
                      }}
                      style={{
                        width: '80px',
                        padding: '4px 8px',
                        backgroundColor: isDarkTheme ? '#0f172a' : '#ffffff',
                        color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                        border: `1px solid ${isDarkTheme ? '#475569' : '#cbd5e1'}`,
                        borderRadius: '5px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center',
                        outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>seconds</span>
                  </div>
                ) : (
                  <strong>{originalDashboardRouteDataRefreshSeconds} seconds</strong>
                )}
              </td>
              {canModifyTenantSettings && (
              <td>
                <div className="action-buttons">
                  {editingDashboardRouteDataRefresh ? (
                    <>
                      <button
                        className="btn-icon"
                        onClick={handleCancelDashboardRouteDataRefresh}
                        style={{ background: isDarkTheme ? '#334155' : '#e2e8f0', color: isDarkTheme ? '#94a3b8' : '#64748b' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={handleSaveDashboardRouteDataRefresh}
                        disabled={
                          savingDashboardRouteDataRefresh ||
                          dashboardRouteDataRefreshSeconds === originalDashboardRouteDataRefreshSeconds
                        }
                        style={{
                          opacity:
                            savingDashboardRouteDataRefresh ||
                            dashboardRouteDataRefreshSeconds === originalDashboardRouteDataRefreshSeconds
                              ? 0.5
                              : 1,
                          cursor:
                            savingDashboardRouteDataRefresh ||
                            dashboardRouteDataRefreshSeconds === originalDashboardRouteDataRefreshSeconds
                              ? 'not-allowed'
                              : 'pointer',
                        }}
                      >
                        {savingDashboardRouteDataRefresh ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  ) : canModifyTenantSettings ? (
                    <button className="btn-icon btn-edit" onClick={() => setEditingDashboardRouteDataRefresh(true)}>
                      Edit
                    </button>
                    ) : null}
                </div>
              </td>
              )}
            </tr>

            {/* Halt Report: Min Halt Time */}
            <tr>
              <td>
                <strong>Halt Report – Min Halt Time</strong>
              </td>
              <td style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b', lineHeight: '1.4' }}>
                Minimum halt duration (seconds) to be included in the halt report. Default: 1200s (20 min)
              </td>
              <td>
                {editingHaltMinTime ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="number"
                      min={1}
                      max={86400}
                      step={1}
                      value={haltMinTime}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) setHaltMinTime(val);
                      }}
                      style={{
                        width: '80px',
                        padding: '4px 8px',
                        backgroundColor: isDarkTheme ? '#0f172a' : '#ffffff',
                        color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                        border: `1px solid ${isDarkTheme ? '#475569' : '#cbd5e1'}`,
                        borderRadius: '5px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center',
                        outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>seconds</span>
                  </div>
                ) : (
                  <strong>{originalHaltMinTime} seconds</strong>
                )}
              </td>
              {canModifyTenantSettings && (
              <td>
                <div className="action-buttons">
                  {editingHaltMinTime ? (
                    <>
                      <button
                        className="btn-icon"
                        onClick={handleCancelHaltMinTime}
                        style={{ background: isDarkTheme ? '#334155' : '#e2e8f0', color: isDarkTheme ? '#94a3b8' : '#64748b' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={handleSaveHaltMinTime}
                        disabled={savingHaltMinTime || haltMinTime === originalHaltMinTime}
                        style={{
                          opacity: savingHaltMinTime || haltMinTime === originalHaltMinTime ? 0.5 : 1,
                          cursor: savingHaltMinTime || haltMinTime === originalHaltMinTime ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {savingHaltMinTime ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  ) : canModifyTenantSettings ? (
                    <button className="btn-icon btn-edit" onClick={() => setEditingHaltMinTime(true)}>
                      Edit
                    </button>
                    ) : null}
                </div>
              </td>
              )}
            </tr>

            {/* Halt Report: Max Speed */}
            <tr>
              <td>
                <strong>Halt Report – Max Speed</strong>
              </td>
              <td style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b', lineHeight: '1.4' }}>
                Maximum speed (km/h) at which a vehicle is still considered halted. Default: 2 km/h
              </td>
              <td>
                {editingHaltMaxSpeed ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={haltMaxSpeed}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) setHaltMaxSpeed(val);
                      }}
                      style={{
                        width: '70px',
                        padding: '4px 8px',
                        backgroundColor: isDarkTheme ? '#0f172a' : '#ffffff',
                        color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                        border: `1px solid ${isDarkTheme ? '#475569' : '#cbd5e1'}`,
                        borderRadius: '5px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center',
                        outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>km/h</span>
                  </div>
                ) : (
                  <strong>{originalHaltMaxSpeed} km/h</strong>
                )}
              </td>
              {canModifyTenantSettings && (
              <td>
                <div className="action-buttons">
                  {editingHaltMaxSpeed ? (
                    <>
                      <button
                        className="btn-icon"
                        onClick={handleCancelHaltMaxSpeed}
                        style={{ background: isDarkTheme ? '#334155' : '#e2e8f0', color: isDarkTheme ? '#94a3b8' : '#64748b' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={handleSaveHaltMaxSpeed}
                        disabled={savingHaltMaxSpeed || haltMaxSpeed === originalHaltMaxSpeed}
                        style={{
                          opacity: savingHaltMaxSpeed || haltMaxSpeed === originalHaltMaxSpeed ? 0.5 : 1,
                          cursor: savingHaltMaxSpeed || haltMaxSpeed === originalHaltMaxSpeed ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {savingHaltMaxSpeed ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  ) : canModifyTenantSettings ? (
                    <button className="btn-icon btn-edit" onClick={() => setEditingHaltMaxSpeed(true)}>
                      Edit
                    </button>
                    ) : null}
                </div>
              </td>
              )}
            </tr>

            {/* Halt Report: Min Distance */}
            <tr>
              <td>
                <strong>Halt Report – Min Distance</strong>
              </td>
              <td style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b', lineHeight: '1.4' }}>
                Minimum drift distance (meters) before a halt is considered ended. Default: 5m
              </td>
              <td>
                {editingHaltMinDistance ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      step={1}
                      value={haltMinDistance}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) setHaltMinDistance(val);
                      }}
                      style={{
                        width: '80px',
                        padding: '4px 8px',
                        backgroundColor: isDarkTheme ? '#0f172a' : '#ffffff',
                        color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                        border: `1px solid ${isDarkTheme ? '#475569' : '#cbd5e1'}`,
                        borderRadius: '5px',
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center',
                        outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>meters</span>
                  </div>
                ) : (
                  <strong>{originalHaltMinDistance} meters</strong>
                )}
              </td>
              {canModifyTenantSettings && (
              <td>
                <div className="action-buttons">
                  {editingHaltMinDistance ? (
                    <>
                      <button
                        className="btn-icon"
                        onClick={handleCancelHaltMinDistance}
                        style={{ background: isDarkTheme ? '#334155' : '#e2e8f0', color: isDarkTheme ? '#94a3b8' : '#64748b' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={handleSaveHaltMinDistance}
                        disabled={savingHaltMinDistance || haltMinDistance === originalHaltMinDistance}
                        style={{
                          opacity: savingHaltMinDistance || haltMinDistance === originalHaltMinDistance ? 0.5 : 1,
                          cursor: savingHaltMinDistance || haltMinDistance === originalHaltMinDistance ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {savingHaltMinDistance ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  ) : canModifyTenantSettings ? (
                    <button className="btn-icon btn-edit" onClick={() => setEditingHaltMinDistance(true)}>
                      Edit
                    </button>
                    ) : null}
                </div>
              </td>
              )}
            </tr>

            {isDevMode && (
              <tr>
                <td>
                  <strong>Convert speed from knots to km/h</strong>
                </td>
                <td style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b', lineHeight: '1.4' }}>
                  When enabled, speed values (stored in knots) are displayed as km/h. Default: Off
                </td>
                <td>
                  {editingConvertKnotsToKmph ? (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={convertKnotsToKmph}
                        onChange={(e) => setConvertKnotsToKmph(e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2196f3' }}
                      />
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: convertKnotsToKmph
                          ? (isDarkTheme ? '#6ee7b7' : '#059669')
                          : (isDarkTheme ? '#f87171' : '#dc2626'),
                      }}>
                        {convertKnotsToKmph ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  ) : (
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: '5px',
                      fontSize: '13px',
                      fontWeight: 600,
                      backgroundColor: originalConvertKnotsToKmph
                        ? (isDarkTheme ? 'rgba(16,185,129,0.15)' : '#ecfdf5')
                        : (isDarkTheme ? 'rgba(239,68,68,0.15)' : '#fef2f2'),
                      color: originalConvertKnotsToKmph
                        ? (isDarkTheme ? '#6ee7b7' : '#059669')
                        : (isDarkTheme ? '#fca5a5' : '#dc2626'),
                      border: originalConvertKnotsToKmph
                        ? (isDarkTheme ? '1px solid rgba(16,185,129,0.3)' : '1px solid #d1fae5')
                        : (isDarkTheme ? '1px solid rgba(239,68,68,0.3)' : '1px solid #fecaca'),
                    }}>
                      {originalConvertKnotsToKmph ? 'Enabled' : 'Disabled'}
                    </span>
                  )}
                </td>
                {canModifyTenantSettings && (
                <td>
                  <div className="action-buttons">
                    {editingConvertKnotsToKmph ? (
                      <>
                        <button
                          className="btn-icon"
                          onClick={handleCancelConvertKnotsToKmph}
                          style={{ background: isDarkTheme ? '#334155' : '#e2e8f0', color: isDarkTheme ? '#94a3b8' : '#64748b' }}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={handleSaveConvertKnotsToKmph}
                          disabled={savingConvertKnotsToKmph || convertKnotsToKmph === originalConvertKnotsToKmph}
                          style={{
                            opacity: savingConvertKnotsToKmph || convertKnotsToKmph === originalConvertKnotsToKmph ? 0.5 : 1,
                            cursor: savingConvertKnotsToKmph || convertKnotsToKmph === originalConvertKnotsToKmph ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {savingConvertKnotsToKmph ? 'Saving...' : 'Save'}
                        </button>
                      </>
                    ) : canModifyTenantSettings ? (
                      <button className="btn-icon btn-edit" onClick={() => setEditingConvertKnotsToKmph(true)}>
                        Edit
                      </button>
                    ) : null}
                  </div>
                </td>
                )}
              </tr>
            )}

          </tbody>
        </table>
      </div>
    </div>
  );

  if (skipLayout) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
};

export default ConfigSettings;
